import { m } from "@/paraglide/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { FilterIcon, GemIcon2 } from "~/components/icons";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import Nodata from "~/components/ui/no-data";
import { PaginationPro } from "~/components/ui/pagination";
import { type Column } from "~/components/ui/table-pro";
import { BaseResponse } from "~/lib/request";
import { formatThousands, formatTimestampAuto } from "~/lib/utils";
import {
	GemHistoryItem,
	GemHistoryResponse,
	GemSourceType,
	gemHistory,
} from "./-api";

export const Route = createFileRoute("/$lang/profile/gem")({
	component: ProfileGem,
});

// 频道与枚举映射
const allChannels = [
	{ label: m.profile_gem_recharge(), value: GemSourceType.RECHARGE },
	{
		label: m.profile_gem_recommend_reward(),
		value: GemSourceType.RECOMMENDATION_REWARD,
	},
	{
		label: m.profile_gem_new_user_reward(),
		value: GemSourceType.NEW_USER_REWARD,
	},
	{ label: m.profile_gem_split(), value: GemSourceType.CREATION_SHARING },
	{ label: m.profile_gem_play_cost(), value: GemSourceType.VISIT_CONSUMPTION },
];

const PAGE_SIZE = 10;

function ProfileGem() {
	// 默认全选
	const [channelFilter, setChannelFilter] = useState<GemSourceType[]>(
		allChannels.map((c) => c.value),
	);

	const [page, setPage] = useState(1);

	const { data, isLoading } = useQuery<BaseResponse<GemHistoryResponse>>({
		queryKey: ["gemHistory", channelFilter, page],
		queryFn: () =>
			gemHistory({
				page_num: page,
				page_size: PAGE_SIZE,
				source_type: channelFilter.join(","),
				creation_id: "",
			}),
	});

	const records: GemHistoryResponse["list"] = data?.data?.list ?? [];
	const total: number = data?.data?.total ?? 0;
	const pageCount = Math.ceil(total / PAGE_SIZE);

	type RecordType = GemHistoryItem;
	const columns: Column<RecordType>[] = [
		{
			key: "amount",
			title: m.profile_gem_amount(),
			render: (v: number) => (
				<span>{v > 0 ? `+${formatThousands(v)}` : formatThousands(v)}</span>
			),
		},
		{
			key: "source_type",
			title: (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className="flex justify-center cursor-pointer items-center gap-1 font-medium bg-transparent outline-none"
							style={{ minWidth: 80 }}
						>
							{m.common_operation()} <FilterIcon />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="center"
						className="min-w-[162px] p-0 border-0"
					>
						{allChannels.map((ch) => (
							<DropdownMenuCheckboxItem
								key={ch.value}
								checked={channelFilter.includes(ch.value)}
								onCheckedChange={(checked) => {
									setChannelFilter((prev) => {
										const next = checked
											? [...prev, ch.value]
											: prev.filter((c) => c !== ch.value);
										setPage(1); // 筛选变更时重置为第一页
										return next;
									});
								}}
								className="flex items-center gap-2 border-b border-line1 rounded-none py-2"
							>
								<span className="text-sm">{ch.label}</span>
							</DropdownMenuCheckboxItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			),
			render: (v: string, row) => <span>{row.operation}</span>,
		},
		{
			key: "created_time",
			title: m.profile_gem_time(),
			render: (v: number) => <span>{formatTimestampAuto(v)}</span>,
		},
	];

	return (
		<div className="px-[18px] py-[22px] pb-0">
			<div className="grid grid-cols-12 justify-between items-center text-t3 pb-5 text-sm font-medium border-b border-line1">
				{columns.map((col) => (
					<div key={String(col.key)} className="col-span-4 flex justify-center">
						{col.title}
					</div>
				))}
			</div>
			<div className="min-h-[500px]">
				{records.map((row, index) => (
					<div
						key={row.creation_id + row.created_time + index}
						className="grid grid-cols-12 justify-between items-center text-t1 text-sm font-medium border-b border-line1 last:border-b-0"
					>
						{columns.map((col) => (
							<div
								key={String(col.key)}
								className="col-span-4 flex min-h-10  py-3 items-center gap-[6px] justify-center"
							>
								<div className="line-clamp-2 text-center">
									{col.render
										? col.render(row[col.key as keyof RecordType], row)
										: row[col.key as keyof RecordType]}
								</div>
								{col.key === "amount" && (
									<GemIcon2 className="w-[18px] h-[18px]" />
								)}
							</div>
						))}
					</div>
				))}
				{isLoading && (
					<div className="text-center text-t3 text-sm py-6">
						{m.common_loading()}
					</div>
				)}
				{!isLoading && records.length === 0 && <Nodata />}
				{pageCount >= 1 && (
					<div className="flex justify-center py-4">
						<PaginationPro
							page={page}
							pageCount={pageCount}
							onPageChange={setPage}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
